/**
 * navigation/authFlowContext.tsx
 * The one cross-phase transition the auth screens need: "credentials are good
 * and verified — leave the auth stack and enter the app." Within-stack moves
 * (login → signup, etc.) use useNavigation(); only this jump crosses the
 * auth/app boundary owned by the root navigator.
 */

import React, { createContext, useContext } from 'react';
import type { ShellRoute } from './types';

export interface EnterAppOptions {
  /** Land the shell on a specific route instead of the default (tenant/home).
   *  Used by the post-onboarding "Go Live" flow to drop the user on Settings. */
  initialShellRoute?: ShellRoute;
}

export interface AuthFlowValue {
  /** Verified sign-in succeeded → refresh session and switch to the app shell. */
  enterApp: (opts?: EnterAppOptions) => void;
}

const AuthFlowContext = createContext<AuthFlowValue | null>(null);

export const AuthFlowProvider = AuthFlowContext.Provider;

export function useAuthFlow(): AuthFlowValue {
  const ctx = useContext(AuthFlowContext);
  if (!ctx) throw new Error('useAuthFlow must be used within an AuthFlowProvider');
  return ctx;
}
